import type { FieldPath, FieldPathValue, FieldValues } from 'react-hook-form';
import { z } from 'zod';

// Generic schema for the WithOtherDetail type
const withOtherDetailSchema = <T extends z.ZodEnum<[string, ...string[]]>>(valueSchema: T) =>
  z
    .object({
      value: valueSchema,
      otherDetail: z.string().optional(),
    })
    .refine(
      (data) => {
        if (data.value === 'other') {
          return typeof data.otherDetail === 'string' && data.otherDetail.length > 0;
        }
        return true;
      },
      {
        message: 'otherDetail is required when value is "other"',
        path: ['otherDetail'],
      },
    );

// Enum schemas for all the options
const MonitorSizeOptionSchema = z.enum(['lt-17-inch', 'ge-17-inch', 'other'], {
  required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง',
});
const CpuOptionSchema = z.enum(
  ['intel-core-i3-ge-2ghz', 'intel-core-i5-ge-2ghz', 'intel-core-i7-ge-2ghz', 'other'],
  { required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง' },
);
const RamSizeOptionSchema = z.enum(['lt-4gb', '4gb', '8gb', '16gb', 'gt-16gb', 'other'], {
  required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง',
});
const OsOptionSchema = z.enum(['windows-10', 'windows-11', 'macos-ge-10.14', 'other'], {
  required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง',
});
const StorageOptionSchema = z.enum(['500gb', '1tb', '2tb', 'other'], {
  required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง',
});
const WebBrowserOptionSchema = z.enum(['google-chrome', 'edge', 'firefox', 'safari', 'other'], {
  required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง',
});
const SebVersionStatusOptionSchema = z.enum(['seb-ge-3.9.3', 'seb-lt-3.9.3'], {
  required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง',
});
const UpdateCadenceOptionSchema = z.enum(['monthly', 'quarterly', 'semiannual', 'other'], {
  required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง',
});
const InternetSpeedOptionSchema = z.enum(['100-50', '100-100', '200-50', '200-100', 'other'], {
  required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง',
});
const AntivirusProgramOptionSchema = z.enum(
  [
    'windows-defender',
    'norton',
    // eslint-disable-next-line @cspell/spellchecker
    'kaspersky',
    'avast',
    'other',
  ],
  { required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง' },
);
const AntivirusConfigForSebOptionSchema = z.enum(
  ['allow-seb', 'not-installed', 'not-available', 'not-configured'],
  { required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง' },
);
const SyncedWithThaiServerOptionSchema = z.enum(['synced', 'not-synced'], {
  required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง',
});
const UsbPortCheckOptionSchema = z.enum(['checked', 'not-checked', 'not-available'], {
  required_error: 'กรุณาเลือกตัวเลือกใดตัวเลือกหนึ่ง',
});

// Schema for the ComputerDetails section
const ComputerDetailsSchema = z.object({
  monitor: withOtherDetailSchema(MonitorSizeOptionSchema),
  cpu: withOtherDetailSchema(CpuOptionSchema),
  ram: withOtherDetailSchema(RamSizeOptionSchema),
  os: withOtherDetailSchema(OsOptionSchema),
  storage: withOtherDetailSchema(StorageOptionSchema),
  webBrowser: withOtherDetailSchema(WebBrowserOptionSchema),
  sebVersionStatus: SebVersionStatusOptionSchema,
  osUpdateCadence: withOtherDetailSchema(UpdateCadenceOptionSchema),
  computerSpecImages: z
    .array(z.string())
    .min(1, { message: 'กรุณาแนบรูปถ่ายอย่างน้อย 1 รูป' })
    .default([]),
  computerSeatImages: z
    .array(z.string())
    .min(1, { message: 'กรุณาแนบรูปถ่ายอย่างน้อย 1 รูป' })
    .default([]),
});

// Schema for the InternetDetails section
const InternetDetailsSchema = z.object({
  internetSpeed: withOtherDetailSchema(InternetSpeedOptionSchema),
  speedTestImages: z
    .array(z.string())
    .min(1, { message: 'กรุณาแนบรูปถ่ายอย่างน้อย 1 รูป' })
    .default([]),
});

// Schema for the SecurityDetails section
const SecurityDetailsSchema = z.object({
  antivirusProgram: withOtherDetailSchema(AntivirusProgramOptionSchema),
  antivirusConfigForSEB: AntivirusConfigForSebOptionSchema,
  isSyncedWithThaiServer: SyncedWithThaiServerOptionSchema,
  timeSyncImages: z
    .array(z.string())
    .min(1, { message: 'กรุณาแนบรูปถ่ายอย่างน้อย 1 รูป' })
    .default([]),
  usbPortCheck: UsbPortCheckOptionSchema,
  antivirusUpdateCadence: withOtherDetailSchema(UpdateCadenceOptionSchema),
  robotTestImages: z
    .array(z.string())
    .min(1, { message: 'กรุณาแนบรูปถ่ายอย่างน้อย 1 รูป' })
    .default([]),
});

// Main schema for the entire form
export const ExamLocationRequestRoomComputerInfoFormSchema = z.object({
  computerDetails: ComputerDetailsSchema,
  internetDetails: InternetDetailsSchema,
  securityDetails: SecurityDetailsSchema,
});

export type ExamLocationRequestRoomComputerInfoFeatures =
  | 'monitor'
  | 'cpu'
  | 'ram'
  | 'os'
  | 'storage'
  | 'webBrowser'
  | 'sebVersionStatus'
  | 'osUpdateCadence'
  | 'computerSpecImages'
  | 'computerSeatImages'
  | 'internetSpeed'
  | 'speedTestImages'
  | 'antivirusProgram'
  | 'antivirusConfigForSEB'
  | 'isSyncedWithThaiServer'
  | 'timeSyncImages'
  | 'usbPortCheck'
  | 'antivirusUpdateCadence'
  | 'robotTestImages';

export type ExamLocationRequestRoomComputerInfoFeaturesWithOtherDetail =
  | 'monitor'
  | 'cpu'
  | 'ram'
  | 'os'
  | 'storage'
  | 'webBrowser'
  | 'osUpdateCadence'
  | 'internetSpeed'
  | 'antivirusProgram'
  | 'antivirusUpdateCadence';

type BasePathsWithOther<T extends FieldValues> = {
  [K in FieldPath<T>]: FieldPathValue<T, K> extends { otherDetail?: string } ? K : never;
}[FieldPath<T>];

type OtherDetailPaths<T extends FieldValues> = Extract<
  `${BasePathsWithOther<T>}.otherDetail`,
  FieldPath<T>
>;

export type ExamLocationRequestRoomComputerInfoBasePathsWithOther =
  BasePathsWithOther<ExamLocationRequestRoomComputerInfoFormInputs>;

export type ExamLocationRequestRoomComputerInfoOtherDetailPath =
  OtherDetailPaths<ExamLocationRequestRoomComputerInfoFormInputs>;

export const EXAM_LOCATION_REQUEST_ROOM_COMPUTER_INFO_OTHER_DETAIL_PATHS = [
  'computerDetails.monitor.otherDetail',
  'computerDetails.cpu.otherDetail',
  'computerDetails.ram.otherDetail',
  'computerDetails.os.otherDetail',
  'computerDetails.storage.otherDetail',
  'computerDetails.webBrowser.otherDetail',
  'computerDetails.osUpdateCadence.otherDetail',
  'internetDetails.internetSpeed.otherDetail',
  'securityDetails.antivirusProgram.otherDetail',
  'securityDetails.antivirusUpdateCadence.otherDetail',
] as const satisfies ReadonlyArray<ExamLocationRequestRoomComputerInfoOtherDetailPath>;

export type ExamLocationRequestRoomComputerInfoFeatureTypes =
  | 'computerDetails'
  | 'internetDetails'
  | 'securityDetails';

export type ExamLocationRequestRoomComputerInfoFormInputs = z.infer<
  typeof ExamLocationRequestRoomComputerInfoFormSchema
>;
